import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Participant,
  RedactParticipantResponse,
  participantsService,
} from '../services/ParticipantsService';
import { logger } from 'utils/logger';

const log = logger.getLogger('RedactParticipantDialog');

interface Props {
  open: boolean;
  participant: Participant | null;
  onClose: () => void;
}

type Phase = 'confirm' | 'loading' | 'results';

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'PARTIAL':
      return 'warning';
    case 'FAILURE':
      return 'error';
    default:
      return 'default';
  }
};

const RedactParticipantDialog: React.FC<Props> = ({ open, participant, onClose }) => {
  const [phase, setPhase] = useState<Phase>('confirm');
  const [result, setResult] = useState<RedactParticipantResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase('confirm');
    setResult(null);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (phase === 'loading') return;
    reset();
    onClose();
  }, [phase, reset, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!participant) return;
    setPhase('loading');
    setError(null);
    try {
      const response = await participantsService.redactParticipant({
        participantId: participant.id,
      });
      setResult(response);
      setPhase('results');
    } catch (err: any) {
      log.error('Redaction request failed', err);
      setError(err?.message || 'Redaction request failed.');
      setPhase('results');
    }
  }, [participant]);

  if (!participant) return null;

  const hasErrors =
    !!result?.services?.some((s) => (s.errors?.length ?? 0) > 0) ||
    result?.status === 'FAILURE' ||
    result?.status === 'PARTIAL' ||
    !!error;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={phase === 'results' ? 'md' : 'sm'}
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {phase === 'confirm' && (
            <WarningAmberIcon color="error" sx={{ fontSize: '2rem' }} />
          )}
          {phase === 'loading' && (
            <CircularProgress size={24} sx={{ mr: 0.5 }} />
          )}
          {phase === 'results' &&
            (hasErrors ? (
              <ErrorIcon color="warning" sx={{ fontSize: '2rem' }} />
            ) : (
              <CheckCircleIcon color="success" sx={{ fontSize: '2rem' }} />
            ))}
          <Typography variant="h6" component="span" sx={{ ml: 1.5 }}>
            {phase === 'confirm' && 'Redact Participant'}
            {phase === 'loading' && 'Redacting Participant...'}
            {phase === 'results' && 'Redaction Results'}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          size="small"
          disabled={phase === 'loading'}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {phase === 'confirm' && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>This action is permanent and cannot be reversed.</AlertTitle>
              All Personally Identifiable Information for this participant will be
              permanently redacted from the related services:
              <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                <li>participants</li>
                <li>feedback</li>
                <li>reporting</li>
                <li>transaction</li>
              </Box>
            </Alert>
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Participant ID:</strong> {participant.id}
              </Typography>
              <Typography variant="body2">
                <strong>Email / Client ID:</strong> {participant.clientId}
              </Typography>
              {(participant.first || participant.last) && (
                <Typography variant="body2">
                  <strong>Name:</strong>{' '}
                  {[participant.first, participant.middle, participant.last]
                    .filter(Boolean)
                    .join(' ')}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {phase === 'loading' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              Please wait while we redact this participant across all services.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This may take a moment. Do not close this dialog.
            </Typography>
          </Box>
        )}

        {phase === 'results' && (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Redaction request failed</AlertTitle>
                {error}
                <Box sx={{ mt: 1 }}>
                  Please forward the participant id <strong>{participant.id}</strong>,
                  email <strong>{participant.clientId}</strong>, and this error
                  message to the Engineering team.
                </Box>
              </Alert>
            )}

            {result && (
              <>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Participant ID:</strong> {result.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Original Email:</strong> {result.originalEmail}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Redacted Email:</strong> {result.email}
                  </Typography>
                  <Box>
                    <strong>Status:</strong>{' '}
                    <Chip
                      label={result.status}
                      color={statusColor(result.status)}
                      size="small"
                      sx={{ ml: 1, fontWeight: 600 }}
                    />
                  </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Service Breakdown
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Redacted Counts</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Errors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.services?.map((s, idx) => (
                      <TableRow key={`${s.service}-${idx}`}>
                        <TableCell>{s.service}</TableCell>
                        <TableCell>
                          <Chip
                            label={s.status}
                            color={statusColor(s.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {s.redactedCounts && Object.keys(s.redactedCounts).length > 0 ? (
                            <Stack spacing={0.25}>
                              {Object.entries(s.redactedCounts).map(([k, v]) => (
                                <Typography key={k} variant="caption">
                                  {k}: {v}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {s.errors && s.errors.length > 0 ? (
                            <Stack spacing={0.25}>
                              {s.errors.map((e, i) => (
                                <Typography
                                  key={i}
                                  variant="caption"
                                  color="error.main"
                                  sx={{ wordBreak: 'break-word' }}
                                >
                                  {e}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {hasErrors && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>Some services reported errors</AlertTitle>
                    Please forward the participant id{' '}
                    <strong>{result.id}</strong>, email{' '}
                    <strong>{result.originalEmail}</strong>, and the errors above
                    to the Engineering team so they can investigate and complete
                    the redaction.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {phase === 'confirm' && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ textTransform: 'none', minWidth: 80, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="error"
              sx={{ textTransform: 'none', minWidth: 80, fontWeight: 600 }}
            >
              Redact
            </Button>
          </>
        )}
        {phase === 'loading' && (
          <Button disabled variant="outlined" sx={{ textTransform: 'none' }}>
            Please wait...
          </Button>
        )}
        {phase === 'results' && (
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{ textTransform: 'none', minWidth: 80, fontWeight: 600 }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RedactParticipantDialog;
